import {
  defaultTheme,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React from 'react'
import VirtualCardDecliningBalanceActive from 'src/assets/images/VirtualCard-DecliningBalance-Active.svg'
import VirtualCardDecliningBalanceInactive from 'src/assets/images/VirtualCard-DecliningBalance-Inactive.svg'
import VirtualCardDecliningBalancePending from 'src/assets/images/VirtualCard-DecliningBalance-Pending.svg'
import VirtualCardDigitalCreditCardActive from 'src/assets/images/VirtualCard-DigitalCreditCard-Active.svg'
import VirtualCardDigitalCreditCardInactive from 'src/assets/images/VirtualCard-DigitalCreditCard-Inactive.svg'
import VirtualCardDigitalCreditCardPending from 'src/assets/images/VirtualCard-DigitalCreditCard-Pending.svg'
import VirtualCardGuestActive from 'src/assets/images/VirtualCard-Guest-Active.svg'
import VirtualCardGuestInactive from 'src/assets/images/VirtualCard-Guest-Inactive.svg'
import VirtualCardGuestPending from 'src/assets/images/VirtualCard-Guest-Pending.svg'
import VirtualCardGuestRecurring from 'src/assets/images/VirtualCard-Guest-Recurring.svg'
import VirtualCardGuestRecurringInactive from 'src/assets/images/VirtualCard-Guest-Recurring-Inactive.svg'
import VirtualCardRecurringActive from 'src/assets/images/VirtualCard-Recurring-Active.svg'
import VirtualCardRecurringInactive from 'src/assets/images/VirtualCard-Recurring-Inactive.svg'
import VirtualCardRecurringPending from 'src/assets/images/VirtualCard-Recurring-Pending.svg'
import VirtualCardStandardActive from 'src/assets/images/VirtualCard-Standard-Active.svg'
import VirtualCardStandardInactive from 'src/assets/images/VirtualCard-Standard-Inactive.svg'
import VirtualCardStandardPending from 'src/assets/images/VirtualCard-Standard-Pending.svg'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { translateNode } from 'src/localization/translateNode'
import { AddressModel, AddressResponse } from 'src/models/address.models'
import { CardImageModel, CardImageResponse } from 'src/models/cardImage.models'
import { CreditCardListResponse } from 'src/models/creditCard.models'
import { IssuerListModel, IssuerListResponse } from 'src/models/issuer.models'
import {
  ReceiptAttachmentModel,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import {
  RecurrenceModel,
  RecurrenceResponse,
} from 'src/models/recurrence.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { toDollars } from 'src/utils/currency.utils'
import { convertUTCTimezone } from 'src/utils/date.utils'
import { css } from 'styled-components'

export enum VirtualCardStatus {
  Pending = 'PENDING',
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
  Rejected = 'REJECTED',
  Consumed = 'CONSUMED',
  Closed = 'CLOSED',
  Approved = 'APPROVED',
}

export enum ApprovalStatus {
  AwaitingApproverRuleApproval = 'AWAITING_APPROVER_RULE_APPROVAL',
  AwaitingCreditCardUserApproval = 'AWAITING_CREDIT_CARD_USER_APPROVAL',
}

export interface VirtualCardStatistics {
  clearedTransactionDebitAmount: number
  clearedTransactionCreditAmount: number
}

export enum VirtualCardType {
  Recurring,
  Direct,
  Standard,
  RecurringGuest,
  BillPay,
  DecliningBalance,
  IssuerCycle,
}

export interface IssuerCycleConfig {
  nextAt: string // ISO8601 date
  type: string // "MONTHLY"
}

export enum VirtualCardNumberFormat {
  Cardnumber14 = 'cardnumber14',
  Cardnumber15 = 'cardnumber15',
  Cardnumber16 = 'cardnumber16',
}

interface VirtualCardFeatureResponse {
  recurrence?: boolean
  customAddress?: boolean
  singleExactPay?: boolean
  walletsEnabled?: WalletType[]
  balanceLocked?: boolean
}

export enum WalletType {
  ApplePay = 'APPLEPAY',
  GooglePay = 'GOOGLEPAY',
}

class VirtualCardFeatureModel {
  public recurrence?: boolean
  public customAddress?: boolean
  public singleExactPay?: boolean
  public walletsEnabled?: WalletType[]
  public balanceLocked?: boolean

  public constructor(data: VirtualCardFeatureResponse) {
    this.recurrence = data.recurrence
    this.customAddress = data.customAddress
    this.singleExactPay = data.singleExactPay
    this.walletsEnabled = data.walletsEnabled
    this.balanceLocked = data.balanceLocked
  }
}

export interface VirtualCardPendingRevisionResponse {
  limitCents?: number
  balanceCents?: number
  validFrom?: string
  validTo?: string
  activeUntil?: string
  recurs?: boolean
  recurrence?: RecurrenceResponse
  receiptAttachments?: ReceiptAttachmentResponse[]
  currency?: ExtendCurrency
  id?: string
  pendingRevisionId: string | null
}

export class VirtualCardPendingRevisionModel {
  public limitCents?: number
  public balanceCents?: number
  public validFrom?: string
  public validTo?: string
  public activeUntil?: string
  public recurs?: boolean
  public recurrence?: RecurrenceModel
  public receiptAttachments?: ReceiptAttachmentResponse[]
  public currency?: ExtendCurrency
  public id?: string
  public pendingRevisionId: string | null

  public constructor(data: VirtualCardPendingRevisionResponse) {
    this.limitCents = data.limitCents
    this.balanceCents = data.balanceCents
    this.validFrom = data.validFrom
    this.validTo = data.validTo
    this.activeUntil = data.activeUntil
    this.recurs = data.recurs
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.receiptAttachments = data.receiptAttachments
    this.currency = data.currency
    this.id = data.id
    this.pendingRevisionId = data.pendingRevisionId
  }

  public get balance(): number | undefined {
    return this.balanceCents !== undefined
      ? toDollars(this.balanceCents)
      : undefined
  }

  public balanceLabel(
    currency: ExtendCurrency,
  ): React.ReactElement | undefined {
    return this.balance !== undefined ? (
      <Crncy currency={currency} amount={this.balance} />
    ) : undefined
  }

  public validToTimezonedMoment(timezone: string): Moment | undefined {
    return this.validTo !== undefined
      ? moment.tz(this.validTo, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public validToTimezonedString(timezone: string): string | undefined {
    const mom = this.validToTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public activeUntilTimezonedMoment(timezone: string): Moment | undefined {
    return this.activeUntil !== undefined
      ? moment.tz(this.activeUntil, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public activeUntilTimezonedString(timezone: string): string | undefined {
    const mom = this.activeUntilTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public validFromTimezonedMoment(timezone: string): Moment | undefined {
    return this.validFrom !== undefined
      ? moment.tz(this.validFrom, timezone)
      : undefined
  }

  public validFromTimezonedString(timezone: string): string | undefined {
    const mom = this.validFromTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }
}

export interface VirtualCardBillPayMetaResponse {
  vendorName: string
  invoiceNumber?: string
  invoiceDate?: string
  shared: boolean
  sharedAt?: string
  vendor?: UserListResponse
}

export class VirtualCardBillPayMetaModel {
  public vendorName: string
  public invoiceNumber?: string
  public invoiceDate?: string
  public shared: boolean
  public sharedAt?: string
  public vendor?: UserListModel

  public constructor(data: VirtualCardBillPayMetaResponse) {
    this.vendorName = data.vendorName
    this.invoiceNumber = data.invoiceNumber
    this.invoiceDate = data.invoiceDate
    this.shared = data.shared
    this.sharedAt = data.sharedAt
    this.vendor =
      data.vendor !== undefined ? new UserListModel(data.vendor) : undefined
  }
}

export interface VirtualCardListResponse {
  id: string
  status: VirtualCardStatus
  recipientId: string
  recipient?: UserListResponse
  cardholderId: string
  cardholder?: UserListResponse
  cardImage: CardImageResponse
  displayName: string
  expires: string
  currency: ExtendCurrency
  limitCents: number
  balanceCents: number
  spentCents: number
  awaitingBudget?: boolean
  last4: string
  numberFormat: VirtualCardNumberFormat
  validFrom: string
  validTo: string
  inactiveSince?: string
  timezone: string
  creditCardId?: string
  recurs: boolean
  recurrence?: RecurrenceResponse
  pending?: VirtualCardPendingRevisionResponse
  notes?: string
  updatedAt: string
  createdAt: string
  address?: AddressResponse
  direct?: boolean
  features: VirtualCardFeatureResponse
  creditCard?: CreditCardListResponse
  activeUntil: string // TODO: this may be wrong?
  customerSupportCode?: string
  creditCardDisplayName?: string
  companyName?: string
  issuer?: IssuerListResponse
  nextApprover?: string
  awaitingApprovalStatus?: ApprovalStatus
  receiptRulesExempt?: boolean
  billPayMeta?: VirtualCardBillPayMetaResponse
  balanceLocked?: boolean
  issuerCycle?: boolean
  issuerCycleConfig?: IssuerCycleConfig | null
  lowLimitAlert?: {
    alertEnabled: boolean
    amountThresholdCents: number | null
  }
}

export enum VirtualCardSort {
  displayName = 'displayName',
  type = 'type',
  status = 'status',
  balanceCents = 'balanceCents',
}

export class VirtualCardListModel {
  public id: string
  public status: VirtualCardStatus
  public recipientId: string
  public cardholderId: string
  public cardholder?: UserListModel
  public cardImage: CardImageModel
  public displayName: string
  public expires: string
  public currency: ExtendCurrency
  public limitCents: number
  public balanceCents: number
  public spentCents: number
  public awaitingBudget?: boolean
  public last4: string
  public numberFormat: VirtualCardNumberFormat
  public validFrom: string
  public validTo: string
  public inactiveSince?: string
  public timezone: string
  public creditCardId?: string
  public recurs: boolean
  public recurrence?: RecurrenceModel
  public pending?: VirtualCardPendingRevisionModel
  public notes?: string
  public updatedAt: string
  public createdAt: string
  public address?: AddressModel
  public direct?: boolean
  public features: VirtualCardFeatureModel
  public activeUntil: string
  public customerSupportCode?: string
  public creditCardDisplayName?: string
  public companyName?: string
  public issuer?: IssuerListModel
  public recipient?: UserListModel
  public nextApprover?: string
  public awaitingApprovalStatus?: ApprovalStatus
  public receiptRulesExempt?: boolean
  public billPayMeta?: VirtualCardBillPayMetaModel
  public balanceLocked?: boolean
  public issuerCycle?: boolean
  public issuerCycleConfig?: IssuerCycleConfig | null
  public lowLimitAlert?: {
    alertEnabled: boolean
    amountThresholdCents: number | null
  }

  public constructor(data: VirtualCardListResponse) {
    this.id = data.id
    this.status = data.status
    this.recipientId = data.recipientId
    this.cardholderId = data.cardholderId
    this.cardholder =
      data.cardholder !== undefined
        ? new UserListModel(data.cardholder)
        : undefined
    this.cardImage = new CardImageModel(data.cardImage)
    this.displayName = data.displayName
    this.expires = data.expires
    this.currency = data.currency
    this.limitCents = data.limitCents
    this.balanceCents = data.balanceCents
    this.spentCents = data.spentCents
    this.awaitingBudget = data.awaitingBudget
    this.last4 = data.last4
    this.numberFormat = data.numberFormat
    this.validFrom = data.validFrom
    this.validTo = data.validTo
    this.inactiveSince = data.inactiveSince
    this.timezone = data.timezone
    this.creditCardId = data.creditCardId
    this.recurs = data.recurs
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.pending =
      data.pending !== undefined
        ? new VirtualCardPendingRevisionModel(data.pending)
        : undefined
    this.notes = data.notes
    this.updatedAt = data.updatedAt
    this.createdAt = data.createdAt
    this.address =
      data.address !== undefined ? new AddressModel(data.address) : undefined
    this.direct = data.direct
    this.features = new VirtualCardFeatureModel(data.features)
    // API returns balanceLocked at root level; propagate to features for consistency
    if (
      data.balanceLocked !== undefined &&
      this.features.balanceLocked === undefined
    ) {
      this.features.balanceLocked = data.balanceLocked
    }
    this.activeUntil = data.activeUntil
    this.customerSupportCode = data.customerSupportCode
    this.creditCardDisplayName = data.creditCardDisplayName
    this.companyName = data.companyName
    this.issuer =
      data.issuer !== undefined ? new IssuerListModel(data.issuer) : undefined
    this.recipient =
      data.recipient !== undefined
        ? new UserListModel(data.recipient)
        : undefined
    this.nextApprover = data.nextApprover
    this.awaitingApprovalStatus = data.awaitingApprovalStatus
    this.receiptRulesExempt = data.receiptRulesExempt
    this.billPayMeta =
      data.billPayMeta !== undefined
        ? new VirtualCardBillPayMetaModel(data.billPayMeta)
        : undefined
    this.balanceLocked = data.balanceLocked
    this.issuerCycle = data.issuerCycle
    this.issuerCycleConfig = data.issuerCycleConfig
    this.lowLimitAlert = data.lowLimitAlert
  }

  public get isActive(): boolean {
    return this.status === VirtualCardStatus.Active
  }

  public get isPending(): boolean {
    return this.status === VirtualCardStatus.Pending
  }

  public get isInactive(): boolean {
    return !this.isActive && !this.isPending
  }

  public get isCancelledOrRejectedRequest(): boolean {
    return (
      (this.status === VirtualCardStatus.Cancelled ||
        this.status === VirtualCardStatus.Rejected) &&
      this.creditCardId === undefined
    )
  }

  public get isInactiveAndNotCancelledOrRejectedRequest(): boolean {
    return this.isInactive && !this.isCancelledOrRejectedRequest
  }

  public get isConsumed(): boolean {
    // subset of inactive
    return this.status === VirtualCardStatus.Consumed
  }

  public get isClosed(): boolean {
    return this.status === VirtualCardStatus.Closed
  }

  public get isBillPay(): boolean {
    return this.billPayMeta !== undefined
  }

  public get isIssuerCycle(): boolean {
    return this.issuerCycle === true
  }

  public get isDecliningBalance(): boolean {
    return this.balanceLocked === true
  }

  public get truncatedDisplayName(): string {
    return this.displayName.length > 45
      ? `${this.displayName.slice(0, 44)}...`
      : this.displayName
  }

  public get cardType(): VirtualCardType {
    if (this.isIssuerCycle) {
      return VirtualCardType.IssuerCycle
    } else if (this.isDecliningBalance) {
      return VirtualCardType.DecliningBalance
    } else if (this.isBillPay) {
      return VirtualCardType.BillPay
    } else if (this.features.balanceLocked) {
      return VirtualCardType.DecliningBalance
    } else if (this.recurs && this.direct) {
      return VirtualCardType.RecurringGuest
    } else if (this.recurs && !this.direct) {
      return VirtualCardType.Recurring
    } else if (!this.recurs && this.direct) {
      return VirtualCardType.Direct
    } else {
      return VirtualCardType.Standard
    }
  }

  public get cardTypeLabel(): React.ReactElement {
    if (this.isIssuerCycle) {
      return translateNode('Recurring')
    } else if (this.isDecliningBalance) {
      return translateNode('Declining Balance')
    } else if (this.isBillPay) {
      return translateNode('Bill Pay')
    } else if (this.features.balanceLocked) {
      return translateNode('Declining Balance Card')
    } else if (this.recurs && this.direct) {
      return translateNode('Guest Recurring')
    } else if (this.recurs && !this.direct) {
      return translateNode('Recurring')
    } else if (!this.recurs && this.direct) {
      return translateNode('Guest Card')
    } else {
      return translateNode('Standard')
    }
  }

  public get cardTypeLabelSrc(): string {
    if (this.isIssuerCycle) {
      return 'Recurring'
    } else if (this.isDecliningBalance) {
      return 'Declining Balance'
    } else if (this.isBillPay) {
      return 'Bill Pay'
    } else if (this.features.balanceLocked) {
      return 'Declining Balance Card'
    } else if (this.recurs && this.direct) {
      return 'Guest Recurring'
    } else if (this.recurs && !this.direct) {
      return 'Recurring'
    } else if (!this.recurs && this.direct) {
      return 'Guest Card'
    } else {
      return 'Standard'
    }
  }

  public get cardTypeImageSrc(): string {
    if (this.isIssuerCycle) {
      if (this.isPending) return VirtualCardDigitalCreditCardPending
      return this.isActive
        ? VirtualCardDigitalCreditCardActive
        : VirtualCardDigitalCreditCardInactive
    } else if (this.isDecliningBalance) {
      if (this.isPending) return VirtualCardDecliningBalancePending
      return this.isActive
        ? VirtualCardDecliningBalanceActive
        : VirtualCardDecliningBalanceInactive
    } else if (this.isBillPay) {
      // bill pay
      if (this.isPending) return VirtualCardGuestPending
      return this.isActive ? VirtualCardGuestActive : VirtualCardGuestInactive
    } else if (this.features.balanceLocked) {
      // declining balance cards
      if (this.isPending) return VirtualCardDecliningBalancePending
      return this.isActive
        ? VirtualCardDecliningBalanceActive
        : VirtualCardDecliningBalanceInactive
    } else if (this.recurs) {
      // all recurring cards
      if (this.direct) {
        return this.isActive
          ? VirtualCardGuestRecurring
          : VirtualCardGuestRecurringInactive
      } else {
        if (this.isPending) return VirtualCardRecurringPending
        return this.isActive
          ? VirtualCardRecurringActive
          : VirtualCardRecurringInactive
      }
    } else if (this.direct) {
      // all guest cards
      if (this.isPending) return VirtualCardGuestPending
      return this.isActive ? VirtualCardGuestActive : VirtualCardGuestInactive
    } else {
      // standard cards
      if (this.isPending) return VirtualCardStandardPending
      return this.isActive
        ? VirtualCardStandardActive
        : VirtualCardStandardInactive
    }
  }

  public get psuedoStatusLabel(): React.ReactElement {
    return this.isPending
      ? translateNode('Pending')
      : this.isActive
        ? translateNode('Active')
        : this.isConsumed || this.isClosed
          ? translateNode('Closed')
          : translateNode('Inactive')
  }

  public get pseudoStatusModifiers(): Record<string, boolean> {
    return {
      isActive: this.isActive,
      isPending: this.isPending,
      isInactive: this.isInactive,
    }
  }

  public get expirationString(): string {
    return moment.utc(this.expires).format('MM/YY')
  }

  public get inactiveSinceTimezonedMoment(): Moment | undefined {
    return this.inactiveSince !== undefined
      ? convertUTCTimezone(this.timezone, this.inactiveSince)
      : undefined
  }

  public get inactiveSinceTimezonedString(): string | undefined {
    return this.inactiveSinceTimezonedMoment?.format('ll')
  }

  public get activeUntilTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.activeUntil).subtract(
      1,
      'millisecond',
    )
  }

  public get activeUntilTimezonedString(): string {
    return this.activeUntilTimezonedMoment.format('ll')
  }

  public get invoiceDateTimezonedMoment(): Moment | undefined {
    return this.billPayMeta?.invoiceDate
      ? convertUTCTimezone(this.timezone, this.billPayMeta?.invoiceDate)
      : undefined
  }

  public get invoiceDateTimezonedString(): string | undefined {
    return this.invoiceDateTimezonedMoment?.format('ll')
  }

  public get sharedDateTimezonedMoment(): Moment | undefined {
    return this.billPayMeta?.sharedAt
      ? convertUTCTimezone(this.timezone, this.billPayMeta?.sharedAt)
      : undefined
  }

  public get sharedDateTimezonedString(): string | undefined {
    return this.sharedDateTimezonedMoment?.format('ll')
  }

  public get validToTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.validTo).subtract(
      1,
      'millisecond',
    )
  }

  public get validToTimezonedString(): string {
    return this.validToTimezonedMoment.format('ll')
  }

  public get updatedAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.updatedAt)
  }

  public get updatedAtTimezonedString(): string {
    return this.updatedAtTimezonedMoment.format('l')
  }

  public get createdAtTimezonedMoment(): Moment {
    return convertUTCTimezone(this.timezone, this.createdAt)
  }

  public get createdAtTimezonedString(): string {
    return this.createdAtTimezonedMoment.format('l')
  }

  public get limit(): number {
    return toDollars(this.limitCents)
  }

  public get limitLabel(): React.ReactElement {
    return (
      <Crncy
        currency={this.currency}
        amount={this.limit}
        maximumFractionDigits={this.isIssuerCycle ? 0 : 2}
      />
    )
  }

  public get balance(): number {
    return toDollars(this.balanceCents)
  }

  // Always shows cents — transactions can produce fractional amounts (e.g., $244.15).
  // Contrast with limitLabel which suppresses cents for IssuerCycle (limits are whole dollars).
  public get balanceLabel(): React.ReactElement {
    return <Crncy currency={this.currency} amount={this.balance} />
  }

  public get redactedVcn(): string {
    switch (this.numberFormat) {
      case VirtualCardNumberFormat.Cardnumber14:
        return `**********${this.last4}`
      case VirtualCardNumberFormat.Cardnumber15:
        return `***********${this.last4}`
      case VirtualCardNumberFormat.Cardnumber16:
        return `************${this.last4}`
      default:
        return `************${this.last4}`
    }
  }

  public get activeUntilOrPseudoStatusLabel(): React.ReactElement | string {
    return this.isActive
      ? this.activeUntilTimezonedString
      : this.psuedoStatusLabel
  }

  public updateFlyoutTitleString(
    permissions: VirtualCardPermissionsModel,
  ): string {
    const awaitingFinalApproval =
      this.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval

    if (this.isBillPay && awaitingFinalApproval) {
      return 'Bill Pay Card Request'
    } else if (this.isBillPay) {
      return 'Update Bill Pay Card'
    } else if (awaitingFinalApproval) {
      return 'Virtual Card Request'
    } else if (permissions.canUpdateVirtualCard && this.isPending) {
      return 'Approve Virtual Card Request'
    } else if (permissions.canUpdateVirtualCard && this.pending !== undefined) {
      return 'Approve Virtual Card Update'
    } else if (permissions.canUpdateVirtualCard) {
      return 'Update Virtual Card'
    } else {
      return 'Request Virtual Card Update'
    }
  }

  public updateFlyoutTitle(
    permissions: VirtualCardPermissionsModel,
  ): React.ReactElement {
    return translateNode(this.updateFlyoutTitleString(permissions))
  }

  public updateFormTopText(
    permissions: VirtualCardPermissionsModel,
  ): React.ReactElement | undefined {
    return this.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval ? (
      <SoftAlertBox
        css={css`
          padding: 16px 20px;
          margin-bottom: 20px;
          background-color: ${styleConstants.colors.yellow_background_light};
        `}
      >
        <span
          css={css`
            ${getFontCss(
              12,
              defaultTheme.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {translateNode(
            "The virtual card request is pending final approval from your organization's Card Approver",
          )}
        </span>
      </SoftAlertBox>
    ) : permissions.canUpdateVirtualCard ? (
      this.isPending ? (
        translateNode(
          'Before you can approve a new card request, you need to select an account below.',
        )
      ) : this.pending !== undefined ? (
        translateNode(
          'Review the request below (highlight in green as "Requested"). You can approve or decline the changes, or make additional updates before approving.',
        )
      ) : (
        translateNode(
          'Any changes you make below will go into effect as soon as you press "Update Virtual Card".',
        )
      )
    ) : (
      translateNode(
        'Update requests require approval by the card creator. While awaiting approval, you can use your card normally and even change your update request.',
      )
    )
  }

  public updateFormButtonText(
    permissions: VirtualCardPermissionsModel,
    isCardApprover?: boolean,
  ): React.ReactElement {
    return permissions.canUpdateVirtualCard || isCardApprover
      ? this.isPending
        ? translateNode('Approve Card Request')
        : this.pending !== undefined
          ? translateNode('Approve Update Request')
          : translateNode('Update Virtual Card')
      : translateNode('Request Update')
  }

  public isBillPayShareable(currentUserId: string): boolean {
    return (
      // card must be a bill pay card
      this.isBillPay &&
      // current user must be cardholder
      this.cardholderId === currentUserId &&
      // not already shared
      this.billPayMeta?.shared === false
    )
  }

  public get billPayVendorEmail(): string | undefined {
    if (!this.isBillPay) return undefined
    return this.billPayMeta?.vendor?.email
  }
}

export enum ScreeningStatus {
  Pass = 'PASS',
  Fail = 'FAIL',
  Forbidden = 'FORBIDDEN',
  NotApplicable = 'NOT_APPLICABLE',
  NeedsMoreInfo = 'NEEDS_MORE_INFO',
  ServiceUnavailable = 'SERVICE_UNAVAILABLE',
}

export enum ToastType {
  ApproveRequestOrCreate = 'CREATE',
  ApproveUpdate = 'APPROVE_UPDATE',
  BillPayApprove = 'BILL_PAY_APPROVE',
}
export interface ScreeningResponse {
  status: ScreeningStatus
}
export interface VirtualCardSecureData {
  vcn?: string
  securityCode?: string
  receiptAttachments?: ReceiptAttachmentResponse[]
  screeningRequired?: boolean
  screening?: ScreeningResponse
}

export type VirtualCardSecureResponse = VirtualCardListResponse &
  VirtualCardSecureData

export class VirtualCardSecureModel extends VirtualCardListModel {
  public vcn?: string
  public securityCode?: string
  public receiptAttachments?: ReceiptAttachmentModel[]
  public screeningRequired?: boolean
  public screening?: ScreeningResponse

  public constructor(data: VirtualCardSecureResponse) {
    super(data)
    this.vcn = data.vcn
    this.securityCode = data.securityCode
    this.receiptAttachments = data.receiptAttachments?.map(
      (att) => new ReceiptAttachmentModel(att),
    )
    this.screeningRequired = data.screeningRequired
    this.screening = data.screening
  }

  public get activeOrRedactedVcn(): string {
    return this.vcn || this.redactedVcn
  }

  public get formattedCardNumber(): string {
    switch (this.numberFormat) {
      case VirtualCardNumberFormat.Cardnumber14: {
        return [
          this.activeOrRedactedVcn.slice(0, 4),
          this.activeOrRedactedVcn.slice(4, 10),
          this.activeOrRedactedVcn.slice(10, 14),
        ].join(' ')
      }
      case VirtualCardNumberFormat.Cardnumber15: {
        return [
          this.activeOrRedactedVcn.slice(0, 4),
          this.activeOrRedactedVcn.slice(4, 10),
          this.activeOrRedactedVcn.slice(10, 15),
        ].join(' ')
      }
      case VirtualCardNumberFormat.Cardnumber16: {
        return [
          this.activeOrRedactedVcn.slice(0, 4),
          this.activeOrRedactedVcn.slice(4, 8),
          this.activeOrRedactedVcn.slice(8, 12),
          this.activeOrRedactedVcn.slice(12, 16),
        ].join(' ')
      }
      default:
        return ''
    }
  }
}

export enum BulkVirtualCardJobStatus {
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
}

export enum BulkVirtualCardJobActionType {
  Update = 'UPDATE',
  Deactivate = 'DEACTIVATE',
  Close = 'CLOSE',
}
